/**
 * Chat Service - LiveKit Integration
 * 
 * Integrates with Labeah AI chat service using LiveKit text streams
 */

import { Room, RoomEvent, Track } from 'livekit-client';
import keepAlive from './keepAlive.js';

class ChatService {
  constructor() {
    this.isConnected = false;
    this.listeners = [];
    this.sessionResetListeners = []; // Listeners for session reset events
    this.agentStateListeners = []; // Listeners for agent state changes
    this.humanAgentListeners = []; // Listeners for human agent events (join/message)
    
    // Message buffer for early messages (before listeners are registered)
    this.messageBuffer = [];
    this.maxBufferSize = 50; // Keep last 50 messages in buffer
    
    // LiveKit
    this.room = null;
    this.localParticipant = null;
    
    // Configuration
    // Check for token in URL parameters, fallback to env variable
    const urlToken = typeof window !== 'undefined' 
      ? new URLSearchParams(window.location.search).get('token') 
      : null;
    this.shareToken = urlToken || import.meta.env.VITE_SHARE_TOKEN;
    console.log(`🔑 Token source: ${urlToken ? 'URL parameter' : 'environment variable'}`);
    this.domain = typeof window !== 'undefined' ? window.location.hostname : 'app.labeah.ai';
    this.apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    
    // Cached data (verified once on load)
    this.verifiedData = null;
    
    // Session identifiers
    this.identity = null;
    this.roomName = null;
    this.sessionId = null;
    this.currentSessionId = null; // Track current session
    
    // Human agent tracking
    this.humanAgentIdentity = null; // Identity of connected human agent
    
    // Flags to prevent duplicate operations
    this.isInitializing = false;
    this.isConnecting = false;
  }

  /**
   * Initialize service - verify token and get agent details
   */
  async initialize() {
    // Check if already initialized
    if (this.verifiedData) {
      console.log('✅ Already initialized (using cached data)');
      return { success: true };
    }

    // Check if currently initializing
    if (this.isInitializing) {
      console.log('⏳ Initialization in progress, waiting...');
      // Wait for current initialization to complete
      while (this.isInitializing) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return { success: true };
    }

    this.isInitializing = true;

    try {
      console.log('🔐 Verifying token and fetching agent details...');
      
      // Verify token (now includes agent details in response)
      const verifyResponse = await fetch(`${this.apiBaseUrl}/v1/tokens/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: this.shareToken,
          domain: this.domain,
        }),
      });

      if (!verifyResponse.ok) {
        throw new Error(`Token verification failed: ${verifyResponse.status}`);
      }

      this.verifiedData = await verifyResponse.json();
      
      if (!this.verifiedData.valid) {
        throw new Error(`Token invalid: ${this.verifiedData.reason}`);
      }

      console.log('✅ Token verified and agent details loaded:', this.verifiedData);
      console.log('✅ Agent info:', {
        agentName: this.verifiedData.access.agentName,
        agentCode: this.verifiedData.access.agentCode,
        textAgentCode: this.verifiedData.access.textAgentCode,
        audioAgentCode: this.verifiedData.access.audioAgentCode,
      });

      return { success: true };
    } catch (error) {
      console.error('❌ Initialization failed:', error);
      return { success: false, error };
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Get or create persistent user identity
   */
  getUserIdentity() {
    const STORAGE_KEY = 'labeah_waseel_user_identity';
    
    // Try to get existing identity from localStorage
    let savedIdentity = null;
    try {
      savedIdentity = localStorage.getItem(STORAGE_KEY);
      if (savedIdentity) {
        console.log('✅ Using saved user identity:', savedIdentity);
      }
    } catch (error) {
      console.warn('⚠️ localStorage not available:', error);
    }
    
    // If no saved identity, generate new one
    if (!savedIdentity) {
      const timestamp = Date.now();
      const random4Digits = Math.floor(1000 + Math.random() * 9000);
      savedIdentity = `guest-${timestamp}-${random4Digits}`;
      
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, savedIdentity);
        console.log('💾 Saved new user identity:', savedIdentity);
      } catch (error) {
        console.warn('⚠️ Could not save identity to localStorage:', error);
      }
    }
    
    return savedIdentity;
  }

  /**
   * Clear saved user identity (force new identity on next session)
   */
  clearUserIdentity() {
    const STORAGE_KEY = 'labeah_waseel_user_identity';
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.log('🗑️ User identity cleared from storage');
    } catch (error) {
      console.warn('⚠️ Could not clear identity:', error);
    }
  }

  /**
   * Generate unique identifiers for session
   */
  generateSessionIdentifiers() {
    const timestamp = Date.now();
    const random4Digits = Math.floor(1000 + Math.random() * 9000);
    
    // Use persistent identity
    this.identity = this.getUserIdentity();
    this.sessionId = `session-${timestamp}-${Math.floor(1000 + Math.random() * 9000)}`;
    
    // Generate room name with UUID v4
    const uuid = this.generateUUID();
    this.roomName = `chat-${uuid}-${timestamp}-${random4Digits}`;
    
    console.log('🆔 Session identifiers:', {
      identity: this.identity,
      sessionId: this.sessionId,
      roomName: this.roomName
    });
  }

  /**
   * Generate UUID v4
   */
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Generate LiveKit token
   */
  async generateLiveKitToken() {
    try {
      console.log('🎫 Generating LiveKit token...');
      
      this.generateSessionIdentifiers();
      
      const { organizationId, agentId, versionId, textAgentCode, agentCode } = this.verifiedData.access;

      console.log('🔧 Agent configuration:', {
        textAgentCode,
        agentCode,
        fullAgentDetails: this.verifiedData.access,
      });

      const payload = {
        identity: this.identity,
        roomName: this.roomName,
        participantName: this.identity,
        agentName: textAgentCode, // Using textAgentCode for text-based responses
        roomPrefix: 'chat',
        shareToken: this.shareToken,
        domain: this.domain,
        metadata: {
          organization_id: String(organizationId),
          agent_id: agentId,
          agent_version_id: versionId,
          type: 'text', // Explicitly requesting text mode
          channel: 'app',
          direction: 'inbound',
          is_debug: false,
          user_id: this.identity,
          session_id: this.sessionId,
          widget_id: '0',
          // Add explicit text mode flags
          mode: 'text',
          output_modality: 'text',
        },
        permissions: {
          canPublish: true,
          canPublishData: true,
          canSubscribe: true,
          canUpdateOwnMetadata: true,
        },
        storeMapping: false,
      };
      
      console.log('📤 Token request payload:', JSON.stringify(payload, null, 2));

      const response = await fetch(`${this.apiBaseUrl}/v2/commkit/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate token: ${response.status}`);
      }

      const tokenData = await response.json();
      console.log('✅ LiveKit token generated:', tokenData);
      console.log('🔧 Token details:', {
        roomName: tokenData.roomName,
        agentName: tokenData.agentName,
        wsUrl: tokenData.wsUrl,
        metadata: tokenData.metadata,
      });
      
      // Decode and log the JWT token to see what's configured
      try {
        const tokenParts = tokenData.accessToken.split('.');
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('🔐 Decoded JWT payload:', payload);
        console.log('🤖 Room config agents:', payload.roomConfig?.agents);
      } catch (e) {
        console.log('⚠️ Could not decode JWT:', e);
      }

      return tokenData;
    } catch (error) {
      console.error('❌ Failed to generate LiveKit token:', error);
      throw error;
    }
  }

  /**
   * Connect to LiveKit room
   */
  async connect(forceNewSession = false) {
    // Check if already connected
    if (this.isConnected && this.room && !forceNewSession) {
      console.log('✅ Already connected to LiveKit');
      return { success: true };
    }

    // If forcing new session, disconnect first
    if (forceNewSession && this.room) {
      console.log('🔄 Force new session, disconnecting old session...');
      this.disconnect();
    }

    // Check if currently connecting
    if (this.isConnecting) {
      console.log('⏳ Connection in progress, waiting...');
      while (this.isConnecting) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return { success: this.isConnected };
    }

    this.isConnecting = true;

    try {
      // Initialize if not done yet
      if (!this.verifiedData) {
        const initResult = await this.initialize();
        if (!initResult.success) {
          throw new Error('Initialization failed');
        }
      }

      // Generate LiveKit token
      const tokenData = await this.generateLiveKitToken();

      console.log('🔌 Connecting to LiveKit...');

      // Create room instance with text stream support and transcription
      this.room = new Room({
        adaptiveStream: true,
        dynacast: true,
        // Enable automatic track subscription
        autoSubscribe: true,
        // WebSocket timeout settings to keep connection alive
        websocketTimeout: 30000, // 30 seconds
        // Enable audio playback to receive agent's audio
        audioOutput: {
          deviceId: 'default',
        },
      });

      // Set up event listeners BEFORE connecting
      this.setupRoomEventListeners();

      // Connect to room
      await this.room.connect(tokenData.wsUrl, tokenData.accessToken);

      this.localParticipant = this.room.localParticipant;
      this.isConnected = true;

      console.log('✅ Connected to LiveKit room:', this.roomName);
      console.log('👤 Local participant:', this.localParticipant.identity);
      console.log('🎧 Text stream handlers active for: lk.chat, lk.transcription');
      
      // Log all current remote participants and their tracks
      this.room.remoteParticipants.forEach((participant) => {
        console.log('👥 Remote participant:', participant.identity);
        console.log('📦 Track publications:', Array.from(participant.trackPublications.values()).map(p => ({
          kind: p.kind,
          trackName: p.trackName,
          subscribed: !!p.track,
        })));
      });

      // Track new session
      const newSessionId = this.sessionId;
      const hadPreviousSession = this.currentSessionId !== null;
      const isNewSession = this.currentSessionId !== newSessionId;
      
      if (isNewSession) {
        console.log('🆕 New session started:', newSessionId);
        
        const previousSessionId = this.currentSessionId;
        this.currentSessionId = newSessionId;
        
        // Only notify listeners to clear if there was a PREVIOUS session
        // On first connection, we don't want to clear the greeting message
        if (hadPreviousSession) {
          console.log('🗑️ Previous session existed, notifying listeners to clear old session data');
          console.log('🔄 Switching from session:', previousSessionId, 'to:', newSessionId);
          
          // Notify all session reset listeners to clear their state
          this.sessionResetListeners.forEach(callback => {
            console.log('📢 Calling session reset listener');
            callback({
              sessionId: newSessionId,
              roomName: this.roomName,
              identity: this.identity,
            });
          });
        } else {
          console.log('ℹ️ First session, not clearing messages');
        }
      }

      // Start keep-alive to prevent connection timeout
      keepAlive.start(this.room);

      return { success: true };
    } catch (error) {
      console.error('❌ Failed to connect to LiveKit:', error);
      this.isConnected = false;
      return { success: false, error };
    } finally {
      this.isConnecting = false;
    }
  }


  /**
   * Set up LiveKit room event listeners for text streams
   */
  setupRoomEventListeners() {
    if (!this.room) return;

    console.log('🎧 Setting up text stream handlers');
    console.log('🎧 Current listeners count:', this.listeners.length);

    // Register text stream handler for lk.chat (primary handler)
    this.room.registerTextStreamHandler('lk.chat', async (reader, participantInfo) => {
      console.log('📨 [lk.chat] Text stream from:', participantInfo.identity);
      
      // Check if message is from agent (not self)
      const isAgent = participantInfo.identity?.startsWith('agent-') || 
                     participantInfo.identity?.includes('assistant') ||
                     participantInfo.identity !== this.identity;
      
      if (!isAgent || participantInfo.identity === this.identity) {
        console.log('ℹ️ Skipping message from self:', participantInfo.identity);
        return;
      }
      
      try {
        // Read the complete text from the stream
        const message = await reader.readAll();
        
        console.log('📨 Received message (plain text):', message);
        console.log('📢 Active listeners:', this.listeners.length);
        
        // Filter out DEBUG messages
        if (message && message.toLowerCase().includes('debug')) {
          console.log('ℹ️ Skipping DEBUG message');
          return;
        }
        
        // Remove source references [SOURCE:...]
        const cleanedMessage = message ? message.replace(/\[source:[^\]]+\]/gi, '').trim() : message;
        
        // Create the message object
        const messageObj = {
          id: reader.info.id || Date.now(),
          text: cleanedMessage, // Plain text, not parsed
          sender: 'bot',
          timestamp: new Date(reader.info.timestamp).toISOString(),
          participant: participantInfo.identity,
          topic: 'lk.chat',
        };
        
        // Deliver message to listeners OR buffer if no listeners
        this._deliverOrBufferMessage(messageObj);
        
        console.log('✅ Message delivered/buffered');
      } catch (error) {
        console.error('❌ Error processing lk.chat stream:', error);
      }
    });
    
    // Register handler for lk.transcription (note: transcription, not transcript!)
    this.room.registerTextStreamHandler('lk.transcription', async (reader, participantInfo) => {
      console.log('📨 [lk.transcription] Text stream from:', participantInfo.identity);
      
      // Filter out self-messages
      if (participantInfo.identity === this.identity) {
        console.log('ℹ️ Skipping transcription from self');
        return;
      }
      
      try {
        const message = await reader.readAll();
        console.log('📨 Received transcription:', message);
        
        // Filter out DEBUG messages
        if (message && message.toLowerCase().includes('debug')) {
          console.log('ℹ️ Skipping DEBUG message');
          return;
        }
        
        // Remove source references [SOURCE:...]
        const cleanedMessage = message ? message.replace(/\[source:[^\]]+\]/gi, '').trim() : message;
        
        // Create the message object
        const messageObj = {
          id: reader.info.id || Date.now(),
          text: cleanedMessage,
          sender: 'bot',
          timestamp: new Date(reader.info.timestamp).toISOString(),
          participant: participantInfo.identity,
          topic: 'lk.transcription',
        };
        
        // Deliver message to listeners OR buffer if no listeners
        this._deliverOrBufferMessage(messageObj);
      } catch (error) {
        console.error('❌ Error processing lk.transcription stream:', error);
      }
    });
    
    // Register handler for lk.human (human agent messages)
    this.room.registerTextStreamHandler('lk.human', async (reader, participantInfo) => {
      console.log('📨 [lk.human] Text stream from human agent:', participantInfo.identity);
      
      try {
        const message = await reader.readAll();
        console.log('📨 Received human agent message:', message);
        
        // Create the message object for human agent
        const messageObj = {
          id: reader.info.id || Date.now(),
          text: message,
          sender: 'human-agent',
          timestamp: new Date(reader.info.timestamp).toISOString(),
          participant: participantInfo.identity,
          topic: 'lk.human',
        };
        
        // Notify human agent listeners
        this._notifyHumanAgentListeners({
          type: 'message',
          data: messageObj,
        });
        
        // Also deliver to regular message listeners so it appears in chat
        this._deliverOrBufferMessage(messageObj);
        
        console.log('✅ Human agent message delivered');
      } catch (error) {
        console.error('❌ Error processing lk.human stream:', error);
      }
    });
    
    // Catch-all handler for debugging
    this.room.registerTextStreamHandler(undefined, async (reader, participantInfo) => {
      console.log('📨 [CATCH-ALL] Text stream on topic:', reader.info.topic, 'from:', participantInfo.identity);
    });
    
    console.log('✅ All text stream handlers registered (lk.chat, lk.transcription, lk.human, catch-all)');

    // Register byte stream handler for receiving files from human agent
    this.room.registerByteStreamHandler('lk.file', async (reader, participantInfo) => {
      console.log('📁 [lk.file] Byte stream (file) from:', participantInfo.identity);
      
      // Only process files from human agents
      if (!participantInfo.identity?.startsWith('human-agent-')) {
        console.log('ℹ️ Skipping file from non-human-agent:', participantInfo.identity);
        return;
      }
      
      try {
        const info = reader.info;
        console.log('📁 Receiving file:', {
          name: info.name,
          mimeType: info.mimeType,
          size: info.size,
          id: info.id,
        });
        
        // Read the entire file
        const chunks = await reader.readAll();
        const blob = new Blob(chunks, { type: info.mimeType });
        const fileUrl = URL.createObjectURL(blob);
        
        // Determine if it's an image
        const isImage = info.mimeType?.startsWith('image/');
        
        // Create the message object for the received file
        const messageObj = {
          id: info.id || Date.now(),
          text: isImage ? '' : info.name,
          sender: 'human-agent',
          timestamp: new Date(info.timestamp).toISOString(),
          participant: participantInfo.identity,
          topic: 'lk.file',
          fileType: isImage ? 'image' : 'file',
          fileName: info.name,
          fileSize: info.size || blob.size,
          fileMimeType: info.mimeType,
          fileUrl: fileUrl,
          uploadStatus: 'received', // Mark as received
        };
        
        // Notify human agent listeners
        this._notifyHumanAgentListeners({
          type: 'file',
          data: messageObj,
        });
        
        // Deliver to regular message listeners so it appears in chat
        this._deliverOrBufferMessage(messageObj);
        
        console.log('✅ File received and delivered:', info.name);
      } catch (error) {
        console.error('❌ Error processing lk.file byte stream:', error);
      }
    });
    
    console.log('✅ Byte stream handler registered (lk.file)');

    // Log ALL room events for debugging
    Object.values(RoomEvent).forEach(eventName => {
      this.room.on(eventName, (...args) => {
        if (!['ConnectionQualityChanged', 'ActiveDeviceChanged'].includes(eventName)) {
          console.log(`🔔 [EVENT] ${eventName}`, args.length > 0 ? args : '');
        }
      });
    });

    // Handle track subscribed to see what tracks are available
    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('📡 Track subscribed:', {
        kind: track.kind,
        source: track.source,
        name: publication.trackName,
        participant: participant.identity,
      });
      
      // If it's an audio track from the agent, log it
      if (track.kind === Track.Kind.Audio) {
        console.log('🎤 Audio track subscribed from:', participant.identity);
        console.log('🎤 Will listen for transcriptions from this track');
      }
    });
    
    // Handle audio track published by remote participant
    this.room.on(RoomEvent.TrackPublished, (publication, participant) => {
      console.log('📣 Track published by remote participant:', {
        kind: publication.kind,
        source: publication.source,
        trackName: publication.trackName,
        participant: participant.identity,
      });
    });
    
    // Handle transcription received (for audio-to-text)
    this.room.on(RoomEvent.TranscriptionReceived, (transcriptions, participant, publication) => {
      console.log('📝 Transcription received from:', participant?.identity);
      console.log('📝 Number of transcriptions:', transcriptions.length);
      
      transcriptions.forEach((transcription, idx) => {
        console.log(`📝 Transcription #${idx + 1}:`, {
          text: transcription.text,
          language: transcription.language,
          trackId: transcription.trackId,
          isFinal: transcription.final,
        });
        
        // Only process final transcriptions
        if (transcription.final && transcription.text) {
          console.log('✅ Final transcription, notifying listeners');
          
          // Notify listeners
          this.listeners.forEach((callback, index) => {
            console.log(`📢 Calling listener #${index + 1} with transcription`);
            callback({
              id: Date.now(),
              text: transcription.text,
              sender: 'bot',
              timestamp: new Date().toISOString(),
              participant: participant?.identity,
              source: 'transcription',
              trackId: transcription.trackId,
            });
          });
        }
      });
    });

    // Also handle data received as fallback for non-stream data
    this.room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
      console.log(
        `📨 [${new Date().toISOString()}] Data packet received\n` +
        `  From: ${participant?.identity}\n` +
        `  Topic: ${topic}\n` +
        `  Kind: ${kind}\n` +
        `  Size: ${payload.byteLength} bytes\n` +
        `  Active listeners: ${this.listeners.length}`
      );
      
      try {
        const decoder = new TextDecoder();
        const text = decoder.decode(payload);
        console.log('📨 Decoded data packet:', text);
        
        let data;
        try {
          data = JSON.parse(text);
          console.log('📨 Parsed data packet:', data);
        } catch {
          console.log('📨 Data packet is plain text');
          data = { text: text };
        }

        // Notify listeners
        console.log('📢 Notifying', this.listeners.length, 'listener(s) of data packet');
        this.listeners.forEach((callback, index) => {
          console.log(`📢 Calling listener #${index + 1} for data packet`);
          callback({
            id: Date.now(),
            text: data.text || data.message || text,
            sender: 'bot',
            timestamp: data.timestamp || new Date().toISOString(),
            participant: participant?.identity,
            topic: topic,
          });
        });
      } catch (error) {
        console.error('❌ Error parsing received data:', error);
      }
    });

    // Handle track published
    this.room.on(RoomEvent.LocalTrackPublished, (publication, participant) => {
      console.log('📤 Track published:', publication.kind);
    });

    // Handle track unpublished
    this.room.on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
      console.log('📤 Track unpublished:', publication.kind);
    });

    // Handle participant connected
    this.room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log('👤 Participant connected:', participant.identity);
      console.log('👥 Total participants:', this.room.remoteParticipants.size + 1);
      console.log('📋 Participant attributes:', participant.attributes);
      console.log('📋 Participant metadata:', participant.metadata);
      
      // Check if this is a human agent joining
      if (participant.identity?.startsWith('human-agent-')) {
        console.log('🧑‍💼 Human agent joined:', participant.identity);
        
        // Save human agent identity for file sending
        this.humanAgentIdentity = participant.identity;
        
        // Notify human agent listeners about the join
        this._notifyHumanAgentListeners({
          type: 'joined',
          data: {
            id: Date.now(),
            identity: participant.identity,
            timestamp: new Date().toISOString(),
            attributes: participant.attributes,
            metadata: participant.metadata,
          },
        });
      }
      
      // Subscribe to participant's tracks
      participant.trackPublications.forEach((publication) => {
        if (publication.track) {
          console.log('🔗 Auto-subscribed to track:', publication.track.kind, publication.trackName);
        } else {
          console.log('📦 Track publication (not yet subscribed):', publication.kind, publication.trackName);
        }
      });
      
      // Listen for new tracks published by this participant
      participant.on(RoomEvent.TrackPublished, (publication) => {
        console.log('📣 Participant published new track:', {
          kind: publication.kind,
          trackName: publication.trackName,
          participant: participant.identity,
        });
      });
      
      // Listen for track subscribed on this specific participant
      participant.on(RoomEvent.TrackSubscribed, (track, publication) => {
        console.log('📡 Track subscribed from participant:', {
          participant: participant.identity,
          kind: track.kind,
          trackName: publication.trackName,
        });
        
        if (track.kind === Track.Kind.Audio) {
          console.log('🎤 Audio track ready, waiting for transcription or text stream...');
        }
      });
    });

    // Handle participant attributes changed (might contain messages!)
    this.room.on(RoomEvent.ParticipantAttributesChanged, (changedAttributes, participant) => {
      console.log('📝 Participant attributes changed:', participant.identity);
      console.log('📝 Changed attributes:', changedAttributes);
      console.log('📝 All participant attributes:', participant.attributes);
      
      // Check for agent state changes
      if (changedAttributes['lk.agent.state']) {
        const agentState = changedAttributes['lk.agent.state'];
        console.log('🤖 Agent state changed to:', agentState);
        
        // Notify agent state listeners
        this.agentStateListeners.forEach((callback) => {
          callback(agentState);
        });
      }
      
      // Check if there's a message in the attributes
      if (changedAttributes.message || changedAttributes.text || changedAttributes.response) {
        console.log('🎯 Found message in participant attributes!');
        const messageText = changedAttributes.message || changedAttributes.text || changedAttributes.response;
        
        // Notify listeners
        this.listeners.forEach((callback, index) => {
          console.log(`📢 Calling listener #${index + 1} with attribute message`);
          callback({
            id: Date.now(),
            text: messageText,
            sender: 'bot',
            timestamp: new Date().toISOString(),
            participant: participant.identity,
            source: 'participantAttributes',
          });
        });
      }
    });
    
    // Handle participant disconnected
    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log('👋 Participant disconnected:', participant.identity);
      
      // Check if it's the human agent disconnecting
      if (participant.identity?.startsWith('human-agent-')) {
        console.log('🧑‍💼 Human agent left:', participant.identity);
        this.humanAgentIdentity = null;
        
        // Notify human agent listeners about the disconnect
        this._notifyHumanAgentListeners({
          type: 'left',
          data: {
            id: Date.now(),
            identity: participant.identity,
            timestamp: new Date().toISOString(),
          },
        });
      }
    });

    // Handle room disconnected
    this.room.on(RoomEvent.Disconnected, (reason) => {
      console.log('🔌 Disconnected from room. Reason:', reason);
      this.isConnected = false;
      
      // Don't attempt automatic reconnection - wait for user action
      console.log('⚠️ Connection lost. Will reconnect when user sends next message.');
    });
    
    // Handle reconnecting state
    this.room.on(RoomEvent.Reconnecting, () => {
      console.log('🔄 Reconnecting to room...');
    });
    
    // Handle reconnected state  
    this.room.on(RoomEvent.Reconnected, () => {
      console.log('✅ Reconnected to room');
      this.isConnected = true;
    });

    // Handle connection state changes
    this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log('🔄 Connection state:', state);
      
      // Update connection status
      if (state === 'connected') {
        this.isConnected = true;
      } else if (state === 'disconnected') {
        this.isConnected = false;
      }
    });

    // Handle connection quality changes
    this.room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
      console.log('📶 Connection quality:', quality, participant?.identity || 'local');
    });
  }

  /**
   * Deliver message to listeners or buffer it if no listeners are registered
   * This handles the race condition where messages arrive before listeners are set up
   */
  _deliverOrBufferMessage(messageObj) {
    if (this.listeners.length > 0) {
      // Deliver to all listeners
      console.log(`📢 Delivering message to ${this.listeners.length} listener(s)`);
      this.listeners.forEach((callback, index) => {
        console.log(`📢 Calling listener #${index + 1}`);
        callback(messageObj);
      });
    } else {
      // Buffer the message for later delivery
      console.log('📦 No listeners, buffering message for later delivery');
      this.messageBuffer.push(messageObj);
      
      // Keep buffer size under limit
      if (this.messageBuffer.length > this.maxBufferSize) {
        this.messageBuffer.shift(); // Remove oldest message
      }
      console.log(`📦 Buffer size: ${this.messageBuffer.length}`);
    }
  }

  /**
   * Clear the message buffer (called on session reset)
   */
  _clearMessageBuffer() {
    console.log(`🗑️ Clearing message buffer (${this.messageBuffer.length} messages)`);
    this.messageBuffer = [];
  }

  /**
   * Send a message via LiveKit text stream
   * Uses sendText API for proper text streaming
   */
  async sendMessage(message) {
    // Check connection and reconnect if needed
    if (!this.isConnected || !this.room) {
      console.log('⚠️ Not connected, reconnecting...');
      const connectResult = await this.connect();
      if (!connectResult.success) {
        throw new Error('Failed to connect');
      }
    }

    try {
      console.log('📤 Sending message via text stream:', message);
      console.log('📤 Current room state:', {
        connected: this.isConnected,
        roomName: this.roomName,
        identity: this.identity,
        participantCount: this.room.remoteParticipants.size + 1
      });

      // Filter out DEBUG messages
      if (message.toLowerCase().includes('debug')) {
        console.log('ℹ️ Skipping DEBUG message');
        return { success: true, messageId: Date.now() };
      }
      
      // Remove source references [SOURCE:...]
      const cleanedMessage = message.replace(/\[source:[^\]]+\]/gi, '').trim();

      // Send plain text (NOT JSON) to match working implementation
      const result = await this.room.localParticipant.sendText(
        cleanedMessage, // Send plain text directly
        {
          topic: 'lk.chat',
          attributes: {
            messageType: 'user',
            timestamp: new Date().toISOString(),
          },
        }
      );

      console.log('✅ Message sent via text stream (plain text)');
      console.log('📊 Send result:', result);

      return {
        success: true,
        messageId: Date.now(),
      };
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      console.error('❌ Error details:', error.message, error.stack);
      return { success: false, error };
    }
  }

  /**
   * Send a file to the human agent via LiveKit byte stream
   * @param {File} file - The file to send
   * @param {Function} onProgress - Optional progress callback (0-1)
   * @returns {Promise<{success: boolean, fileInfo?: object, error?: Error}>}
   */
  async sendFileToHumanAgent(file, onProgress = null) {
    // Check if human agent is present
    if (!this.humanAgentIdentity) {
      console.error('❌ Cannot send file: No human agent connected');
      return { success: false, error: new Error('No human agent connected') };
    }

    // Check connection and reconnect if needed
    if (!this.isConnected || !this.room) {
      console.log('⚠️ Not connected, reconnecting...');
      const connectResult = await this.connect();
      if (!connectResult.success) {
        return { success: false, error: new Error('Failed to connect') };
      }
    }

    try {
      console.log('📤 Sending file to human agent:', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        targetIdentity: this.humanAgentIdentity,
      });

      // Send file using LiveKit sendFile API
      // Targeting only the human agent using destinationIdentities
      const info = await this.room.localParticipant.sendFile(file, {
        mimeType: file.type,
        topic: 'lk.file', // Topic for file transfers
        destinationIdentities: [this.humanAgentIdentity], // Only send to human agent
        onProgress: (progress) => {
          console.log(`📤 File upload progress: ${Math.ceil(progress * 100)}%`);
          if (onProgress && typeof onProgress === 'function') {
            onProgress(progress);
          }
        },
      });

      console.log('✅ File sent successfully:', {
        streamId: info.id,
        fileName: file.name,
        fileType: file.type,
      });

      return {
        success: true,
        fileInfo: {
          id: info.id,
          name: file.name,
          type: file.type,
          size: file.size,
        },
      };
    } catch (error) {
      console.error('❌ Failed to send file:', error);
      console.error('❌ Error details:', error.message, error.stack);
      return { success: false, error };
    }
  }

  /**
   * Check if human agent is currently connected
   * @returns {boolean}
   */
  isHumanAgentConnected() {
    return !!this.humanAgentIdentity;
  }

  /**
   * Get the identity of the connected human agent
   * @returns {string|null}
   */
  getHumanAgentIdentity() {
    return this.humanAgentIdentity;
  }

  /**
   * Disconnect from LiveKit
   */
  disconnect() {
    console.log('👋 Disconnecting from chat service...');
    
    // Stop keep-alive
    keepAlive.stop();
    
    if (this.room) {
      try {
        // Unregister text stream handlers
        this.room.unregisterTextStreamHandler('lk.chat');
        this.room.unregisterTextStreamHandler('lk.transcription');
        this.room.unregisterTextStreamHandler('lk.human');
        // Unregister byte stream handler
        this.room.unregisterByteStreamHandler('lk.file');
        console.log('🗑️ Text and byte stream handlers unregistered');
        
        this.room.disconnect();
      } catch (error) {
        console.error('Error during disconnect:', error);
      }
      this.room = null;
    }
    
    this.localParticipant = null;
    this.isConnected = false;
    this.isConnecting = false;
    
    // Clear session identifiers
    this.identity = null;
    this.roomName = null;
    this.sessionId = null;
    this.humanAgentIdentity = null; // Clear human agent identity
    // currentSessionId is kept to detect new sessions on next connect
    
    // Clear message buffer on disconnect (old session messages shouldn't carry over)
    this._clearMessageBuffer();
    
    // Don't clear listeners - we might reconnect
    // this.listeners = [];
    // this.sessionResetListeners = [];
    
    console.log('👋 Disconnected from chat service');
  }
  
  /**
   * Start a new session (disconnect old, connect new, clear history)
   */
  async startNewSession(forceNewIdentity = false) {
    console.log('🆕 Starting new LiveKit session...');
    
    // Clear user identity if requested
    if (forceNewIdentity) {
      console.log('🔄 Forcing new user identity...');
      this.clearUserIdentity();
    }
    
    // Disconnect existing session
    if (this.room) {
      this.disconnect();
    }
    
    // Wait a bit for cleanup
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Connect with force new session flag
    return await this.connect(true);
  }
  
  /**
   * Check if connection is alive
   */
  isConnectionAlive() {
    return this.isConnected && this.room && this.room.state === 'connected';
  }

  /**
   * Listen for incoming messages
   */
  onMessage(callback) {
    console.log('📝 Registering message listener. Current count:', this.listeners.length);
    this.listeners.push(callback);
    console.log('📝 New listener count:', this.listeners.length);
    
    // Replay any buffered messages to the new listener
    if (this.messageBuffer.length > 0) {
      console.log(`📦 Replaying ${this.messageBuffer.length} buffered message(s) to new listener`);
      this.messageBuffer.forEach((message, index) => {
        console.log(`📦 Replaying buffered message #${index + 1}:`, message.text?.substring(0, 50) + '...');
        callback(message);
      });
      // Clear buffer after replaying (messages were delivered)
      this._clearMessageBuffer();
    }
    
    // Return unsubscribe function
    return () => {
      console.log('🗑️ Unsubscribing message listener. Current count:', this.listeners.length);
      this.listeners = this.listeners.filter(cb => cb !== callback);
      console.log('🗑️ New listener count:', this.listeners.length);
    };
  }

  /**
   * Listen for session reset events (when new LiveKit session starts)
   */
  onSessionReset(callback) {
    console.log('📝 Registering session reset listener');
    this.sessionResetListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      console.log('🗑️ Unsubscribing session reset listener');
      this.sessionResetListeners = this.sessionResetListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Listen for agent state changes (thinking, speaking, listening)
   */
  onAgentStateChange(callback) {
    console.log('📝 Registering agent state listener');
    this.agentStateListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      console.log('🗑️ Unsubscribing agent state listener');
      this.agentStateListeners = this.agentStateListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Listen for human agent events (join, message)
   * @param {Function} callback - Receives { type: 'joined' | 'message', data: {...} }
   */
  onHumanAgentEvent(callback) {
    console.log('📝 Registering human agent event listener');
    this.humanAgentListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      console.log('🗑️ Unsubscribing human agent event listener');
      this.humanAgentListeners = this.humanAgentListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all human agent listeners of an event
   * @private
   */
  _notifyHumanAgentListeners(event) {
    console.log(`📢 Notifying ${this.humanAgentListeners.length} human agent listener(s) of event:`, event.type);
    this.humanAgentListeners.forEach((callback, index) => {
      console.log(`📢 Calling human agent listener #${index + 1}`);
      callback(event);
    });
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      identity: this.identity,
      roomName: this.roomName,
      sessionId: this.sessionId,
    };
  }
}

// Export singleton instance
export default new ChatService();